<template>
  <div class="bbox-blur-label-select" :data-testid="testId">
    <p v-if="fieldLabel" class="text-body-2 mb-1 font-weight-medium">{{ fieldLabel }}</p>
    <p v-if="hint" class="text-caption text-medium-emphasis mb-2">{{ hint }}</p>

    <v-checkbox
      v-if="allowAllOther"
      v-model="allOtherLocal"
      class="mt-0 pt-0"
      density="compact"
      hide-details
      color="primary"
      :disabled="disabled"
      :label="allOtherLabel"
      data-testid="bbox-blur-all-other-labels"
      @update:model-value="onAllOtherToggle"
    />

    <div v-if="displayChips.length" class="selected-labels">
      <v-chip
        v-for="name in displayChips"
        :key="name"
        size="small"
        closable
        class="selected-chip"
        :disabled="disabled"
        @click:close="removeName(name)"
      >
        {{ chipDisplayName(name) }}
      </v-chip>
    </div>

    <search-label-autocomplete
      v-if="!allOtherLocal"
      :label_type="1"
      :placeholder="searchPlaceholder"
      :prepend_icon="null"
      :clear_on_select="true"
      :disabled="disabled"
      :track_last_used="false"
      data-testid="bbox-blur-label-search"
      @label-selected="addLabel"
    />
  </div>
</template>

<script>
import SearchLabelAutocomplete from '@/components/SearchLabelAutocomplete.vue';
import { BBOX_BLUR_UNBLUR_ALL_OTHER } from '@/utils/sensitiveImageBlur';

export default {
  name: 'BboxBlurLabelSelect',
  components: { SearchLabelAutocomplete },
  emits: ['update:modelValue'],
  props: {
    modelValue: { type: Array, default: () => [] },
    fieldLabel: { type: String, default: '' },
    hint: { type: String, default: '' },
    searchPlaceholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    allowAllOther: { type: Boolean, default: false },
    allOtherLabel: { type: String, default: 'All other labels' },
    testId: { type: String, default: 'bbox-blur-label-select' },
  },
  data() {
    return {
      allOtherLocal: false,
    };
  },
  computed: {
    names() {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
    displayChips() {
      if (this.allOtherLocal) {
        return [BBOX_BLUR_UNBLUR_ALL_OTHER];
      }
      return this.names.filter((n) => n !== BBOX_BLUR_UNBLUR_ALL_OTHER);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        const list = Array.isArray(val) ? val : [];
        this.allOtherLocal = this.allowAllOther
          && list.some((n) => String(n).toLowerCase() === BBOX_BLUR_UNBLUR_ALL_OTHER.toLowerCase());
      },
    },
  },
  methods: {
    chipDisplayName(name) {
      if (String(name).toLowerCase() === BBOX_BLUR_UNBLUR_ALL_OTHER.toLowerCase()) {
        return this.allOtherLabel;
      }
      return name;
    },
    emitNames(next) {
      this.$emit('update:modelValue', next);
    },
    onAllOtherToggle(enabled) {
      if (enabled) {
        this.emitNames([BBOX_BLUR_UNBLUR_ALL_OTHER]);
        return;
      }
      this.emitNames([]);
    },
    addLabel(selected) {
      if (!selected || !selected.name || this.allOtherLocal) {
        return;
      }
      const name = String(selected.name).trim();
      if (!name) {
        return;
      }
      const key = name.toLowerCase();
      if (this.names.some((n) => String(n).toLowerCase() === key)) {
        return;
      }
      this.emitNames([...this.names.filter((n) => n !== BBOX_BLUR_UNBLUR_ALL_OTHER), name]);
    },
    removeName(name) {
      if (String(name).toLowerCase() === BBOX_BLUR_UNBLUR_ALL_OTHER.toLowerCase()) {
        this.allOtherLocal = false;
        this.emitNames([]);
        return;
      }
      this.emitNames(this.names.filter((n) => n !== name));
    },
  },
};
</script>

<style scoped>
.selected-labels {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 6px 0;
}
.selected-chip {
  cursor: default;
}
</style>
